import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang, CATEGORIES } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";
import { ClientBottomNav } from "../components/ClientBottomNav";
import { ReviewsBadge } from "../components/ReviewsBadge";
import { ReviewInviteBanner } from "../components/ReviewInviteBanner";
import api from "../lib/api";
import { LogOut, ShieldCheck, MapPin, Clock } from "lucide-react";

export default function ClientHome() {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const goCategory = (id) => nav(`/richiedi?cat=${id}`);

  return (
    <div className="relative mx-auto min-h-screen max-w-md bg-slate-50 pb-24 shadow-2xl">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/85 px-4 py-3 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white"><Icons.Wrench className="h-4 w-4" /></div>
          <span className="font-display text-lg font-extrabold text-slate-900">ProntoSof<span className="text-orange-500">24</span></span>
        </div>
        <div className="flex items-center gap-2">
          <LangToggle />
          <button data-testid="logout-btn" onClick={() => logout().then(() => nav("/login"))} className="rounded-full border border-slate-200 bg-white p-2 text-slate-500"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" alt="" className="h-44 w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-200">{user?.name}</p>
          <h1 className="font-display text-2xl font-extrabold leading-tight">{t("app_tagline")}</h1>
        </div>
      </div>

      <div className="px-4 py-4">
        <button data-testid="request-intervention-btn" onClick={() => nav("/area-clienti/richiedi")}
          className="card-hover flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 py-4 text-base font-bold text-white shadow-lg shadow-blue-600/25">
          <Icons.Plus className="h-5 w-5" />{t("request_intervention")}
        </button>

        <div className="mt-3 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
          <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" />
          <span>{settings?.emergency_note || "Interventi su Roma"} · <span className="font-semibold">{settings?.hours}</span></span>
        </div>

        <ReviewInviteBanner />
        <ReviewsBadge />

        <h2 className="mt-6 font-display text-lg font-bold text-slate-900">{t("categories")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {CATEGORIES.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.Wrench;
            return (
              <button key={c.id} data-testid={`category-${c.id}`} onClick={() => goCategory(c.id)}
                className="card-hover animate-fade-up flex flex-col items-start gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Icon className="h-5 w-5" /></div>
                <span className="text-sm font-bold leading-tight text-slate-900">{c[lang]}</span>
                <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-bold text-orange-600">{lang === "it" ? c.badge_it : c.badge_en}</span>
              </button>
            );
          })}
        </div>

        <button onClick={() => nav("/area-clienti/ordini")} data-testid="go-my-orders" className="mt-6 flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700">
          <span className="flex items-center gap-2"><Icons.ClipboardList className="h-5 w-5 text-blue-600" />{t("my_orders")}</span>
          <Icons.ChevronRight className="h-5 w-5 text-slate-400" />
        </button>
      </div>
      <ClientBottomNav />
    </div>
  );
}
